import { VALTYPES, VALTYPE_ANY } from '../../../../data/interfaces';
import { createColorMappingAspect, createColorMappingConfigs } from '../../helpers/colorMappingInfo';
import {
  createConfigParamLabelSpace,
  createConfigParamMultipleChoice,
  createConfigParamTypeNumericalColorScheme,
  createConfigParamTypeRange,
} from '../../helpers/configParams';
import { TpChartManifest } from '../interfaces';
import { DataMatrix } from './DataMatrix';

export const manifestDataMatrix: TpChartManifest = {
  id: 'DataMatrix',
  name: 'Heatmap',
  description: `Creates a heatmap of numerical values:
- Rows are entities in the primary data table.
- Columns are selected numerical properties in that table.
- Each cell is colored by the value of the corresponding property for that row entity.

Data setup: the heatmap uses a primary table and a linked table describing the column entities.
- The primary table is selected by \`dataBinding.dataTableId\`. Its rows are the heatmap rows, and each row has an item ID. Add one numeric property per heatmap column, and group those properties together using a shared \`groupId\`.
- Each numerical column property must also specify \`columnCanonicalTypeId\`, set to the linked table's canonical type, and \`columnItemId\`, set to the item ID of the linked-table entity represented by that property (**update required to table manifest**). The \`columnItemId\` is an entity ID, not the numerical property's own \`id\`.
- Set \`dataBinding.columnsGroupId\` to the properties' shared \`groupId\` and \`dataBinding.columnsCanonicalTypeId\` to the linked table's canonical type ID. The chart includes properties matching both values, and matches each property to its linked-table row using \`columnItemId\`.
- Bind \`rowLabelId\` and \`hoverLabelRow\` to properties in the primary table. Bind \`columnLabelId\`, \`sortColumnBy\`, and \`hoverLabelColumn\` to properties in the linked table; \`sortColumnBy\` is optional.

Example: a heatmap of microbial taxon abundances across samples.
- The primary taxa table has one row per taxon, with a unique item ID such as \`taxon_01\`. It may include row properties such as taxon name, family, and phylum.
- Store abundance values in wide format: one numeric property per sample, with each row containing that taxon's abundance for that sample. Put the abundance properties in a shared property group such as \`samples\`. For the property containing abundances for sample \`sample_01\`, set \`groupId: samples\`, \`columnCanonicalTypeId\` to the samples table's canonical type (for example, \`sample\`), and \`columnItemId: sample_01\`. Repeat for each sample. A property's own ID may differ from the sample ID.
- The linked samples table has one row per sample, where the row's item ID (for example, \`sample_01\`) matches the corresponding abundance property's \`columnItemId\`. It can contain sample metadata such as treatment, collection site, or date.
- Bind \`rowLabelId\` to a taxon label and \`columnLabelId\` to a sample label. Bind \`hoverLabelRow\` and \`hoverLabelColumn\` to suitable descriptive properties, and configure \`columnsGroupId: samples\` with \`columnsCanonicalTypeId: sample\`.
`,
  showDataMatrix: true,
  dataMatrixDocumentationGroup: 'columns',
  component: DataMatrix,
  canvasBased: true,
  groups: [
    { groupId: 'rows', groupName: 'Rows' },
    { groupId: 'rowColors', groupName: 'Row colors' },
    { groupId: 'columns', groupName: 'Columns' },
    { groupId: 'cells', groupName: 'Cells' },
  ],
  aspects: [
    {
      id: 'rowLabelId',
      name: 'Row labels',
      groupId: 'rows',
      description: 'Contains the displayed labels for the rows of the data matrix.',
      required: true,
      dataType: VALTYPE_ANY,
    },
    {
      id: 'columnLabelId',
      name: 'Column labels',
      groupId: 'columns',
      description: 'Contains the displayed labels for the columns of the data matrix.',
      required: true,
      onColumnItems: true,
      dataType: [VALTYPES.STRING, VALTYPES.NUMBER, VALTYPES.CATEGORICAL],
    },
    {
      id: 'sortRowBy',
      groupId: 'rows',
      name: 'Sort rows by',
      description: 'Used to sort the rows of the data matrix.',
      dataType: [VALTYPES.DENDROGRAM, VALTYPES.STRING, VALTYPES.NUMBER, VALTYPES.CATEGORICAL],
    },
    {
      id: 'sortColumnBy',
      name: 'Sort columns by',
      groupId: 'columns',
      description: 'Used to sort the columns of the data matrix.',
      onColumnItems: true,
      dataType: [VALTYPES.DENDROGRAM, VALTYPES.STRING, VALTYPES.NUMBER, VALTYPES.CATEGORICAL],
    },
    {
      id: 'hoverLabelRow',
      name: 'Hover label (rows)',
      description: 'Text to be displayed when the mouse hovers over a row.',
      groupId: 'rows',
      required: true,
      dataType: VALTYPE_ANY,
    },
    {
      id: 'hoverLabelColumn',
      name: 'Hover label (columns)',
      description: 'Text to be displayed when the mouse hovers over a column.',
      groupId: 'columns',
      onColumnItems: true,
      required: true,
      dataType: VALTYPE_ANY,
    },
    createColorMappingAspect('rowColor', 'Row color data', 'rowColors', 0.5, 'row legend items'),
  ],
  configParams: [
    {
      id: 'colorSchemeMatrixVals',
      name: 'Color scheme',
      groupId: 'cells',
      paramType: createConfigParamTypeNumericalColorScheme(),
    },
    {
      id: 'gamma',
      name: 'Gamma correction',
      groupId: 'cells',
      paramType: createConfigParamTypeRange(0, 10, 0.1, 5),
    },
    ...createColorMappingConfigs('rowColor', 'Row color', 'rowColors'),
    {
      id: 'labelSpaceRows',
      name: 'Label space (rows)',
      description:
        'Scaling factor that determines how much space in the chart is used by the names of the rows (categorical states).',
      groupId: 'rows',
      paramType: createConfigParamLabelSpace(2), // Default value 2 for backwards compatibility with previouse hardcoded default
    },
    {
      id: 'labelSpaceColumns',
      name: 'Label space (columns)',
      description:
        'Scaling factor that determines how much space in the chart is used by the names of the columns (numerical properties).',
      groupId: 'columns',
      paramType: createConfigParamLabelSpace(2), // Default value 2 for backwards compatibility with previouse hardcoded default
    },
    {
      id: 'colorMissingValues',
      name: 'Color for missing values',
      description: 'Color used for cells for which no value is available.',
      groupId: 'cells',
      paramType: createConfigParamMultipleChoice([
        {
          id: 'minValue',
          name: 'Minimum value color',
        },
        {
          id: 'empty',
          name: 'Leave blank (transparent)',
        },
      ]),
      hidden: true, // This is only for dashboard creators.
    },
  ],
  examples: [
    {
      id: 'datamatrix_1',
    },
    {
      id: 'datamatrix_2',
    },
  ],
};
